import createAuth0Client from "@auth0/auth0-spa-js";
import axios from "axios";
import { isNil } from "lodash";
import {
  UserData,
  maxFavoriteModalClass,
  loginModalClass,
  actionLoginClass,
  actionCreateAccountClass,
} from "../_helpers/constants";
import SessionStorage from "../_helpers/session-storage";

export default class Auth0Helpers {
  constructor() {
    this.userApi = window.auth0Config.userApi;
    this.sessionStorage = new SessionStorage();
    this.url = `${this.userApi}api/v1/users/`;
    this.headers = {
      headers: {
        Authorization: null,
      },
    };
    this.maxfavoritesModal = null;
    this.loginModal = null;
    this.actionLogin = null;
    this.actionSignup = null;
    this.redirectUrl = window.location.origin;
  }

  _clone = (obj) => Object.assign({}, obj);

  _renameKey = (object, key) => {
    const newKey = key.replace(UserData.Namespace, "");
    const clonedObj = this._clone(object);
    const targetKey = clonedObj[key];

    delete clonedObj[key];

    clonedObj[newKey] = targetKey;
    return clonedObj;
  };

  _sanitizeTokenData(token) {
    let tokenData = token;

    const keys = Object.keys(token);

    keys.forEach((key) => {
      if (key.includes(UserData.Namespace)) {
        tokenData = this._renameKey(tokenData, key);
      }
    });

    const sessionData = this.sessionStorage.getUserData();
    if (!isNil(sessionData)) {
      tokenData.firstAccess = sessionData.firstAccess;
      if (!isNil(sessionData.favoriteplayers)) {
        tokenData.favoriteplayers = sessionData.favoriteplayers;
      }
    }

    return tokenData;
  }

  async updateUserData(userId, userMetaData) {
    const data = {
      user_metadata: {
        favoriteplayers: userMetaData.favoriteplayers || [],
      },
    };

    const url = `${this.userApi}api/v1/users/${userId}`;
    const token = await window.auth0.getTokenSilently();
    let updateDone = false;
    try {
      await axios
        .patch(url, data, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then(function (response) {
          if (response.data) {
            updateDone = true;
          }
        })
        .catch(function () {
          updateDone = false;
        });
    } catch (error) {
      console.error("Error updating user data:", error);
      updateDone = false;
    }

    return updateDone;
  }

  async updateFirstAccess(userId, userData) {
    const data = {
      user_metadata: {
        firstAccess: userData.firstAccess,
      },
    };

    const url = `${this.url}${userId}/`;
    const token = await window.auth0.getTokenSilently();
    this.headers.headers.Authorization = `Bearer ${token}`;
    try {
      await axios.patch(url, data, this.headers);
    } catch (error) {
      console.error("Error updating first access:", error);
    }
  }

  async isAuthenticated() {
    if (!window.auth0) {
      await this.configureClient(window);
    }
    return window.auth0.isAuthenticated();
  }

  async isFirstLogin() {
    const userData = await this.getUserData();

    if (userData.firstLogin) {
      return userData.firstLogin;
    }
    return false;
  }

  async getUserData() {
    const user = await window.auth0.getUser();

    if (user) {
      const sanitizedData = this._sanitizeTokenData(user);
      const storageUserData = this.sessionStorage.getUserData();
      if (!isNil(storageUserData)) {
        if (!isNil(storageUserData.firstname)) {
          sanitizedData.firstname = storageUserData.firstname;
        }
        if (!isNil(storageUserData.lastname)) {
          sanitizedData.lastname = storageUserData.lastname;
        }
      }
      return sanitizedData;
    }

    return undefined;
  }

  async getUserFavoritesData() {
    const sessionData = this.sessionStorage.getUserData();

    if (sessionData) {
      return sessionData;
    }

    if (!window.auth0) {
      await this.configureClient(window);
    }

    const isAuthenticated = await this.isAuthenticated();

    if (isAuthenticated) {
      const user = await window.auth0.getUser();

      if (user) {
        const userData = this._sanitizeTokenData(user);
        if (userData.favoriteplayers) {
          return userData;
        } else {
          userData.favoriteplayers = [];
          return userData;
        }
      }
    }
    return undefined;
  }

  async getUserName() {
    const userData = await this.getUserData();

    if (userData.firstname) {
      return userData.firstname;
    }
    return undefined;
  }

  async configureClient(w) {
    if (w.auth0Config) {
      w.auth0 = await createAuth0Client({
        domain: w.auth0Config.domain,
        client_id: w.auth0Config.client,
        audience: w.auth0Config.audience,
        cacheLocation: "localstorage",
        useRefreshTokens: true,
      });
    }
  }

  async login(isLogin = true, redirectUri = "") {
    const loginObj = {
      redirect_uri:
        !isNil(redirectUri) && redirectUri !== ""
          ? redirectUri
          : `${this.redirectUrl}`,
    };
    if (!isLogin) {
      loginObj.screen_hint = "signup";
    }

    await window.auth0.loginWithRedirect(loginObj);
  }

  async logout(redirectUrl = "") {
    const logOutObj = {};
    if (redirectUrl === "") {
      logOutObj.returnTo = this.redirectUrl;
    } else {
      logOutObj.returnTo = redirectUrl;
    }
    await window.auth0.logout(logOutObj);
  }

  initModals() {
    this.maxfavoritesModal = $(maxFavoriteModalClass);
    this.loginModal = $(loginModalClass);
    this.actionLogin = $(this.loginModal).find(actionLoginClass);
    this.actionSignup = $(this.loginModal).find(actionCreateAccountClass);
    this.initModalAction(this.actionLogin);
    this.initModalAction(this.actionSignup, false);
  }

  initModalAction(element, isLogin = true, customRedirect = null) {
    const loginObj = {
      redirect_uri: !isNil(customRedirect)
        ? customRedirect
        : `${window.location.origin}`,
    };
    if (!isLogin) {
      loginObj.screen_hint = "signup";
    }
    element.on("click", async function () {
      await window.auth0.loginWithRedirect(loginObj);
    });
  }
}
